/**
 * Shared pointer/drag state for the 3D lobby canvas.
 *
 * Tracks whether the user is actively dragging the camera (and has moved
 * beyond a small threshold). Consumers read `cameraDragState` to decide:
 *
 *   • CameraDragGuard — hide the cursor **only once the user starts moving**
 *     (so a plain single click never disappears the cursor).
 *   • TileInstanced   — suppress the tile-click handler when the pointer
 *     moved meaningfully between down/up (i.e. the user was rotating the
 *     camera, not trying to select a tile).
 *
 * The state is a mutable module-level object. `BoardCanvas3D` mounts a
 * single `<CameraDragGuard>` that maintains it; only one instance exists at
 * a time so there's no cross-talk.
 */

export const DRAG_THRESHOLD_PX = 4;

export interface CameraDragState {
  /** Left-button pointer is currently held down on the canvas. */
  pointerDown: boolean;
  /** Movement (Manhattan distance) accumulated since the last pointerdown. */
  movedPx: number;
  /** True while `movedPx > DRAG_THRESHOLD_PX` — set on pointermove, cleared
   *  shortly after pointerup so the immediately-following click handler
   *  can still read it and bail out. */
  didDrag: boolean;
  /** Client X coordinate at the last pointerdown. */
  lastX: number;
  /** Client Y coordinate at the last pointerdown. */
  lastY: number;
  /** True while Space is held — `BoardCanvas3D` flips this on/off so the
   *  `<OrbitControls>` left-drag swaps from rotate to pan and the canvas
   *  shows a grab/grabbing cursor (the user is panning the camera). The
   *  flag is module-level so the keyboard listener (in `BoardCanvas3D`)
   *  and any reader outside React can stay in sync without prop drilling. */
  panMode: boolean;
}

export const cameraDragState: CameraDragState = {
  pointerDown: false,
  movedPx: 0,
  didDrag: false,
  lastX: 0,
  lastY: 0,
  panMode: false,
};

/** Clears all drag flags — call after board switches, dev HMR, or any time
 *  the canvas remounts so a stale `pointerDown`/`didDrag` cannot block tile
 *  hover, tooltips, and clicks. */
export function resetCameraDragState() {
  cameraDragState.pointerDown = false;
  cameraDragState.movedPx = 0;
  cameraDragState.didDrag = false;
  cameraDragState.lastX = 0;
  cameraDragState.lastY = 0;
  cameraDragState.panMode = false;
}

/** `true` if the user is actively camera-dragging right now. */
export function isCameraDragging(): boolean {
  return cameraDragState.pointerDown && cameraDragState.didDrag;
}

/** `true` if the pointer moved past the threshold within the current gesture. */
export function didCameraDrag(): boolean {
  return cameraDragState.didDrag;
}
